import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, ArrowLeft, Eye, EyeOff } from 'lucide-react';
import BrandLogo from './BrandLogo';
import { ThemeToggleButton } from './ThemeToggle';
import { EvidenceMarker } from './evidence';
import { Input } from './ui/input';

const HERO_PHOTO = `${process.env.PUBLIC_URL}/hero-damage-photo.jpg`;

// Damage regions on the sample photo, as % of its width and height.
const REGIONS = [
  { n: 1, left: 43, top: 9, width: 10, height: 76 },
  { n: 2, left: 56, top: 40, width: 8, height: 38 },
  { n: 3, left: 54, top: 82, width: 17, height: 9 },
];

function EvidencePanel() {
  return (
    <div aria-hidden="true" className="hidden min-w-0 flex-1 flex-col justify-center gap-7 bg-[#0e1116] p-14 text-[#edeff2] lg:flex">
      <div className="relative aspect-[3/2] overflow-hidden rounded-lg bg-[#0a0c10]">
        <img src={HERO_PHOTO} alt="" className="block h-full w-full object-cover" />
        {REGIONS.map((r) => (
          <div
            key={r.n}
            className="absolute border-2 border-marker shadow-[0_0_0_1px_rgba(14,17,22,0.55)]"
            style={{ left: `${r.left}%`, top: `${r.top}%`, width: `${r.width}%`, height: `${r.height}%` }}
          >
            <EvidenceMarker n={r.n} className="absolute -left-0.5 -top-7" />
          </div>
        ))}
      </div>
      <div className="flex max-w-[520px] flex-col gap-2.5">
        <p className="m-0 font-display text-[32px] font-extrabold leading-[38px] tracking-[-0.015em]">
          Every deposit charge, pinned to a photo.
        </p>
        <p className="m-0 text-[#9aa3af]">Three findings marked on one bedroom wall, each with its severity and cost.</p>
      </div>
    </div>
  );
}

/** Shared frame for sign in, create account, forgot and reset password. */
export default function AuthLayout({ title, subtitle, children, footer, backTo = '/', backLabel = 'Back to realspect.app' }) {
  return (
    <div className="flex min-h-screen bg-paper text-ink">
      <div className="flex min-w-0 flex-1 flex-col px-6 py-8 sm:px-10">
        <div className="flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-2.5 font-display text-xl font-extrabold leading-6 text-ink no-underline">
            <BrandLogo className="h-[34px] w-[34px]" />
            RealSpect
          </Link>
          <ThemeToggleButton />
        </div>

        <main className="flex flex-1 items-center justify-center py-12">
          <div className="flex w-full max-w-[400px] flex-col gap-5">
            <div>
              <h1 className="m-0 font-display text-4xl font-extrabold leading-10 tracking-[-0.015em]">{title}</h1>
              {subtitle && <p className="m-0 mt-2 text-ink-muted">{subtitle}</p>}
            </div>
            {children}
            {footer && <p className="m-0 border-t border-line pt-5 text-ink-muted">{footer}</p>}
          </div>
        </main>

        <Link to={backTo} className="inline-flex items-center gap-1.5 self-start text-sm text-ink-muted no-underline hover:text-ink">
          <ArrowLeft className="h-4 w-4" strokeWidth={1.5} />
          {backLabel}
        </Link>
      </div>
      <EvidencePanel />
    </div>
  );
}

/** Inline error that stays until the next attempt, instead of a disappearing toast. */
export function FormError({ children }) {
  if (!children) return null;
  return (
    <div role="alert" className="flex items-start gap-2.5 rounded-md bg-sev-high-soft px-3.5 py-3 text-[15px] leading-[22px] text-sev-high">
      <AlertTriangle className="mt-px h-5 w-5 shrink-0" strokeWidth={1.5} aria-hidden="true" />
      <span>{children}</span>
    </div>
  );
}

/** Underlined text link with the marker-yellow underline. */
export function TextLink({ to, children, ...props }) {
  return (
    <Link to={to} className="font-bold text-ink underline decoration-marker decoration-2 underline-offset-[3px]" {...props}>
      {children}
    </Link>
  );
}

/** Password field with a show/hide button. */
export function PasswordInput({ id, toggleTestId, ...props }) {
  const [shown, setShown] = useState(false);
  return (
    <div className="relative">
      <Input id={id} type={shown ? 'text' : 'password'} className="h-12 pr-12" {...props} />
      <button
        type="button"
        onClick={() => setShown((s) => !s)}
        aria-label={shown ? 'Hide password' : 'Show password'}
        aria-pressed={shown}
        data-testid={toggleTestId}
        className="absolute right-1 top-1 inline-flex h-10 w-10 items-center justify-center rounded-md text-ink-muted hover:text-ink"
      >
        {shown ? <EyeOff className="h-5 w-5" strokeWidth={1.5} /> : <Eye className="h-5 w-5" strokeWidth={1.5} />}
      </button>
    </div>
  );
}
